import { useState } from "react";
import { Navigate, Route, Routes, Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowUpRight, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { RestaurantBrand, ThemeToggle } from "@/components/RestaurantBrand";
import CreditFooter from "@/components/CreditFooter";
import AdminMenu from "@/pages/admin/AdminMenu";
import api from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";

const EMPTY_FORM = { current: "", next: "", confirm: "" };

function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setForm(EMPTY_FORM); setError(""); setLoading(false); };

  const handleOpenChange = (value) => { if (!value) reset(); onOpenChange(value); };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.next.length < 10) { setError("La nuova password deve contenere almeno 10 caratteri."); return; }
    if (form.next !== form.confirm) { setError("La conferma non coincide con la nuova password."); return; }
    if (form.next === form.current) { setError("La nuova password deve essere diversa da quella attuale."); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("nz_admin_token");
      await api.post(
        "/auth/admin/change-password",
        { current_password: form.current, new_password: form.next },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Password aggiornata correttamente");
      reset();
      onOpenChange(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 401) setError("Password attuale non corretta.");
      else if (status === 400 && detail) setError(detail);
      else setError("Aggiornamento non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle data-testid="change-password-title">Cambia password</DialogTitle>
          <DialogDescription>Inserisci la password attuale e scegline una nuova di almeno 10 caratteri.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="admin-password-form">
          <div className="admin-field">
            <Label htmlFor="current-password">Password attuale</Label>
            <Input id="current-password" type="password" autoComplete="current-password" value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} required data-testid="current-password-input" />
          </div>
          <div className="admin-field">
            <Label htmlFor="new-password">Nuova password</Label>
            <Input id="new-password" type="password" autoComplete="new-password" value={form.next} onChange={e => setForm({ ...form, next: e.target.value })} required minLength={10} data-testid="new-password-input" />
          </div>
          <div className="admin-field">
            <Label htmlFor="confirm-password">Conferma nuova password</Label>
            <Input id="confirm-password" type="password" autoComplete="new-password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} required aria-invalid={Boolean(error)} data-testid="confirm-password-input" />
          </div>
          {error && <p role="alert" className="admin-error" data-testid="change-password-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} data-testid="change-password-cancel">Annulla</Button>
            <Button type="submit" disabled={loading} data-testid="change-password-submit">{loading ? "Aggiornamento…" : "Aggiorna password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPanel() {
  usePageTitle("Gestione menu");
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  if (!localStorage.getItem("nz_admin_token")) return <Navigate to="/admin/login" replace />;
  const logout = () => { localStorage.removeItem("nz_admin_token"); navigate("/admin/login"); };
  return <div className="admin-shell" data-testid="admin-panel">
    <header className="admin-header admin-container">
      <div className="admin-header-brand"><RestaurantBrand testId="admin-logo" /><div><p data-testid="admin-brand-name">Nove Zero Due</p><h1 data-testid="admin-panel-title">Amministrazione</h1></div></div>
      <div className="admin-header-actions"><ThemeToggle /><Button variant="outline" onClick={() => setPwOpen(true)} data-testid="admin-change-password"><KeyRound /><span className="exit-label">Cambia password</span></Button><Button asChild variant="outline" data-testid="admin-view-menu"><Link to="/menu" target="_blank" rel="noreferrer">Vedi menu <ArrowUpRight /></Link></Button><Button variant="ghost" onClick={logout} data-testid="admin-logout" aria-label="Esci"><LogOut /><span className="exit-label">Esci</span></Button></div>
    </header>
    <main className="admin-main admin-container"><Routes><Route path="menu" element={<AdminMenu />} /><Route path="*" element={<Navigate to="/admin/menu" replace />} /></Routes><CreditFooter className="restaurant-credit" /></main>
    <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
  </div>;
}
